import React, { useState } from 'react'

const CONCURRENCY_LIMIT = 10
let BASE_URL = 'https://chant3rm1.com/trk/lead'
if (process.env.NODE_ENV === 'development'){
    BASE_URL = 'http://localhost:5002/trk/lead'
}

const getLabel = (paramType) => {
    //  mbi=click_id, mbd=lead_data
    if (paramType === 'none') return `click_id ${'\u00A0'} lead_data`
    return `${paramType} ${'\u00A0'} click_id ${'\u00A0'} lead_data`
}

const getPlaceholder = (paramType) => {
    if (paramType === 'none') return 'E.g.\n186118092\t9560.6420.88857'
    // paramValue, ClickId, Lead Data
    return 'E.g.\n130\t186118092\t9560.6420.88857'
}

const createUrl = ({ mbm, mbr, paramType, rowCols }) => {
    const mbmEncoded = encodeURIComponent(mbm)
    const mbrEncoded = mbr ? encodeURIComponent(mbr) : null

    let url = `${BASE_URL}?mbm=${mbmEncoded}`
    if (mbrEncoded) url += `&mbr=${mbrEncoded}`

    if (paramType === 'none') {
        // Expect 2 columns: mbi, mbd
        if (rowCols.length < 2) return null
        const mbi = encodeURIComponent(rowCols[0].trim())
        const mbd = encodeURIComponent(rowCols[1].trim())
        return `${url}&mbd=${mbd}&mbi=${mbi}`
    }

    // Expect 3 columns: (mbcm/mbs), mbi, mbd
    if (rowCols.length < 3) return null
    const paramValue = encodeURIComponent(rowCols[0].trim())
    const mbi = encodeURIComponent(rowCols[1].trim())
    const mbd = encodeURIComponent(rowCols[2].trim())
    return `${url}&${paramType}=${paramValue}&mbd=${mbd}&mbi=${mbi}`
}

export default function PostbackFiringTool({ showSystemMessage }) {
    const [mbm, setMbm] = useState('')
    const [mbr, setMbr] = useState('')
    const [paramType, setParamType] = useState('mbcm')
    const [inputRows, setInputRows] = useState('')
    const [urls, setUrls] = useState([])
    const [generateError, setGenerateError] = useState('')
    const [isProcessing, setIsProcessing] = useState(false)

    const paramTypeLabel = getLabel(paramType)
    const inputRowsPlaceholder = getPlaceholder(paramType)
    const pastedRowsCount = inputRows
        ? inputRows.split('\n').map((r) => r.trim()).filter(Boolean).length
        : 0
    const readyUrlsCount = urls.filter((u) => Boolean(u.url)).length
    const skippedRowsCount = urls.filter((u) => u.status === 'skipped').length

    const notify = (type, message) => {
        if (showSystemMessage) return showSystemMessage(type, message)
        alert(message)
    }

    const generateUrls = () => {
        if (isProcessing) return
        setGenerateError('')
        setUrls([])
        const mbmTrim = mbm.trim()
        const mbrTrim = mbr.trim()
        const inputTrim = inputRows.trim()
        if (!mbmTrim) {
            notify('error', 'mbm is required.')
            return
        }

        const rows = inputTrim ? inputTrim.split('\n') : []
        const nextItems = []

        rows.forEach((row, idx) => {
            const rowTrim = row.trim()
            if (!rowTrim) return
            const cols = rowTrim.split('\t')
            const finalUrl = createUrl({
                mbm: mbmTrim,
                mbr: mbrTrim,
                paramType,
                rowCols: cols,
            })

            if (finalUrl) {
                nextItems.push({
                    id: `url-${idx}`,
                    url: finalUrl,
                    status: 'pending',
                    message: '',
                })
                return
            }

            nextItems.push({
                id: `url-${idx}`,
                url: '',
                row: rowTrim,
                status: 'skipped',
                message: '✖ Skipped (invalid row format)',
            })
        })

        const validCount = nextItems.filter((i) => Boolean(i.url)).length
        if (validCount === 0) {
            const expectedCols = paramType === 'none' ? '2' : '3'
            setGenerateError(`No valid rows found. Please ensure rows are tab-delimited with ${expectedCols} columns.`)
        }
        setUrls(nextItems)
    }

    const clearAll = () => {
        if (isProcessing) return
        setMbm('')
        setMbr('')
        setParamType('mbcm')
        setInputRows('')
        setUrls([])
        setGenerateError('')
    }

    const updateItem = (id, patch) => {
        setUrls((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)))
    }

    const processUrls = async () => {
        if (isProcessing) return
        const processableItems = urls.filter((u) => Boolean(u.url))
        if (processableItems.length === 0) {
            notify('error', 'Generate URLs first.')
            return
        }

        setIsProcessing(true)

        setUrls((prev) =>
            prev.map((u) => ({
                ...u,
                status: u.url ? 'pending' : u.status,
                message: u.url ? '' : u.message,
            }))
        )

        const itemsSnapshot = processableItems
        let nextIndex = 0
        const worker = async () => {
            while (true) {
                const idx = nextIndex
                nextIndex += 1
                if (idx >= itemsSnapshot.length) return
                const item = itemsSnapshot[idx]
                updateItem(item.id, { status: 'processing', message: '... Processing' })
                try {
                    const response = await fetch(item.url)                    
                    if (response.ok) {
                        let data = await response.json()
                        if (data.success){
                            updateItem(item.id, { status: 'success', message: '✔ Success' })
                        } else {
                            updateItem(item.id, { status: 'fail', message: `✖ Failed: ${(data.errors && data.errors.message) ? data.errors.message : 'Unknown error'}` })
                        }                        
                    } else {
                        updateItem(item.id, { status: 'fail', message: `✖ Failed` })
                    }
                } catch (e) {
                    updateItem(item.id, { status: 'fail', message: `✖ Error: (${e.message})` })
                }
            }
        }

        const workerCount = Math.min(CONCURRENCY_LIMIT, itemsSnapshot.length)
        const workers = Array.from({ length: workerCount }, () => worker())
        await Promise.all(workers)
        setIsProcessing(false)
        notify('success', 'All processable URLs processed!')
    }

    const getStatusStyle = (status) => {
        if (status === 'success') return { color: '#28a745' }
        if (status === 'fail') return { color: '#dc3545' }
        if (status === 'processing') return { color: '#fd7e14' }
        if (status === 'skipped') return { color: '#6c757d' }
        return {}
    }

    const urlList = urls.map((item) => (
        <div
            key={item.id}
            className="mb-2"
            style={{
                ...getStatusStyle(item.status),
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                fontSize: '12px',
            }}
        >
            {item.url ? item.url : item.row}{' '}
            {item.message ? <span style={{ fontFamily: 'inherit', fontSize: '12px' }}>{item.message}</span> : null}
        </div>
    ))

    return (
        <React.Fragment>
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Manual Postback Firing Tool</h1>
            </div>
            <div className="row">
                <div className="col-xl-6">
                    <div className="card">
                        <div className="card-body">
                            <div className="alert alert-secondary py-2" style={{ fontSize: '12px', marginBottom: 12 }}>
                            <div style={{ marginBottom: 4 }}>Use this tool to manually fire campaign postbacks. Data should be formatted from a spreadsheet and then pasted directly into the input area below.</div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>How to use</div>
                                <div>1) Enter <b>Advertiser ID</b> (required) and <b>Ratename</b> (optional).</div>
                                <div style={{ marginTop: 4 }}>2) Select <b>Parameter Type</b>: <b>mbcm (commission)</b>, <b>mbs (sale)</b>, or <b>None</b>.</div>
                                <div style={{ marginTop: 4 }}>3) Paste data from a google sheet. Example of format is displayed below based on mbcm, mbs, or none.</div>
                                <div style={{ marginTop: 4 }}>4) Click <b>Generate URLs</b> to preview postback URLs.</div>
                                <div style={{ marginTop: 4 }}>5) Click <b>Process</b> to fire requests. Status is shown per URL.</div>
                            </div>
                            <div className="form-group">
                                <label>Advertiser ID:</label>
                                <input className="form-control" type="text" value={mbm} disabled={isProcessing} onChange={(e) => setMbm(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>Ratename:</label>
                                <input className="form-control" type="text" value={mbr} disabled={isProcessing} onChange={(e) => setMbr(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <div style={{ marginTop: 10, marginBottom: 6, fontWeight: 600 }}>Select inputs:</div>
                                <div className="d-flex flex-wrap">
                                    <label className="mr-3 mb-0" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input type="radio" name="paramType" value="mbcm" checked={paramType === 'mbcm'} disabled={isProcessing} onChange={() => setParamType('mbcm')} />
                                        Commission Amount (mbcm)
                                    </label>
                                    <label className="mr-3 mb-0" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input type="radio" name="paramType" value="mbs" checked={paramType === 'mbs'} disabled={isProcessing} onChange={() => setParamType('mbs')} />
                                        Sale Amount (mbs)
                                    </label>
                                    <label className="mr-3 mb-0" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input type="radio" name="paramType" value="none" checked={paramType === 'none'} disabled={isProcessing} onChange={() => setParamType('none')} />
                                        None (fixed rates)
                                    </label>
                                </div>
                            </div>
                            <div className="form-group">
                                <label style={{ marginTop: 10 }}>
                                    Paste tab-delimited rows:<br></br>
                                    <code>{paramTypeLabel}</code>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={8}
                                    placeholder={inputRowsPlaceholder}
                                    disabled={isProcessing}
                                    value={inputRows}
                                    onChange={(e) => setInputRows(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Tab') {
                                            e.preventDefault()
                                            const textarea = e.currentTarget
                                            const start = textarea.selectionStart
                                            const end = textarea.selectionEnd
                                            const newValue = inputRows.substring(0, start) + '\t' + inputRows.substring(end)
                                            setInputRows(newValue)

                                            setTimeout(() => {
                                                textarea.selectionStart = textarea.selectionEnd = start + 1
                                            }, 0)
                                        }
                                    }}
                                />
                            </div>

                            {generateError ? <div className="alert alert-danger py-2">{generateError}</div> : null}

                            <div className="d-flex flex-wrap">
                                <button className="btn btn-primary mr-2" type="button" disabled={isProcessing} onClick={generateUrls}>
                                    Generate URLs
                                </button>
                                <button className="btn btn-primary" type="button" disabled={isProcessing || !urls.some((u) => Boolean(u.url))} onClick={processUrls}>
                                    Process
                                </button>
                                <button className="btn btn-secondary ml-2" type="button" disabled={isProcessing} onClick={clearAll}>
                                    Clear
                                </button>
                            </div>
                            {urls.length ? (
                                <div className="text-muted mt-3" style={{ fontSize: '12px' }}>
                                    Pasted <b>{pastedRowsCount}</b> row(s), <b>{readyUrlsCount}</b> URL(s) ready, <b>{skippedRowsCount}</b> skipped.
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="col-xl-6">
                    <div className="card">
                        <div className="card-body">
                            <div style={{ fontWeight: 600, marginBottom: 10 }}>Generated URLs</div>
                            {urls.length ? urlList : <div className="text-muted">Generate URLs to see them here.</div>}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}
